import { api } from './client.js';

export interface Habit {
  habitId: string;
  name: string;
  emoji: string | null;
  color: string | null;
  order: number;
  createdAt: string;
}

export interface HabitLog {
  habitId: string;
  date: string;
}

export async function listHabits(): Promise<{ habits: Habit[]; logs: HabitLog[] }> {
  return api<{ habits: Habit[]; logs: HabitLog[] }>('/habits');
}

/**
 * Every log ever recorded for one habit. `/habits` only carries a rolling window
 * (see [ADR 0005](../../../docs/adr/0005-bounded-habit-log-reads.md)), which is
 * enough for the grid and the charts but would silently shrink the all-time
 * stats. The stats modal pays for this one, on open.
 */
export async function listHabitLogs(hid: string): Promise<HabitLog[]> {
  const r = await api<{ logs: HabitLog[] }>(`/habits/${hid}/logs`);
  return r.logs;
}

export async function createHabit(input: {
  name: string;
  emoji?: string;
  color?: string;
  order?: number;
}): Promise<Habit> {
  const r = await api<{ habit: Habit }>('/habits', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return r.habit;
}

export async function updateHabit(
  hid: string,
  patch: Partial<Omit<Habit, 'habitId' | 'createdAt'>>
): Promise<Habit> {
  const r = await api<{ habit: Habit }>(`/habits/${hid}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return r.habit;
}

export async function deleteHabit(hid: string): Promise<void> {
  await api<void>(`/habits/${hid}`, { method: 'DELETE' });
}

export async function logHabit(hid: string, date: string): Promise<void> {
  await api<{ log: HabitLog }>(`/habits/${hid}/log/${date}`, { method: 'PUT' });
}

export async function unlogHabit(hid: string, date: string): Promise<void> {
  await api<void>(`/habits/${hid}/log/${date}`, { method: 'DELETE' });
}
